import { apiGetProviderTransactionConfigs } from "@lib/api/provider";
import { ProviderTransactionConfigName } from "@lib/components/MiniComponents/Others";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { createYupSync } from "@lib/helpers/TableHelper";
import { transactionSchema } from "@lib/schemas/transaction";
import {
  AuthRoles,
  TransactionProxyState,
  TransactionProxyStatus,
  TransactionType,
  UseMutationProps,
} from "@lib/types";
import { useQuery } from "@tanstack/react-query";
import { Button, Form, Select, Space, Typography } from "antd";
import { useCallback, useEffect } from "react";
import { useIntl } from "react-intl";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  transaction?: TransactionType;
  role: AuthRoles;
  transactionUpdateMutation: UseMutationProps<Partial<TransactionType>>;
};

export default function ConfigurationForm({
  transaction,
  role,
  transactionUpdateMutation,
}: Props) {
  const [form] = Form.useForm();
  const { messages } = useIntl();

  useEffect(() => {
    if (transaction) {
      form.setFieldsValue(transaction);
    }
  }, [transaction]);

  const {
    data: providerTransactionConfigs,
    isLoading: providerTransactionConfigsLoading,
    isFetching: providerTransactionConfigsFetching,
  } = useQuery({
    queryFn: () =>
      apiGetProviderTransactionConfigs({
        limit: 100,
        join: ["provider"],
        filter: [`providerId||$eq||${transaction?.providerId}`],
      }),
    queryKey: ["provider-transaction-configs-dropdown"],
    enabled:
      (role === AuthRoles.ProviderAgent || role === AuthRoles.Admin) &&
      !!transaction,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 1000 * 60,
  });

  const { mutateAsync: updateTransaction, isPending: isUpdating } =
    transactionUpdateMutation;

  const handleUpdateConfig = useCallback(
    async (values: any) => {
      const data = {
        status: TransactionProxyStatus.Pending,
        state: TransactionProxyState.PendingCustomerApproval,
        ...values,
      };
      await updateTransaction({
        id: transaction?.id,
        ...data,
      });
    },
    [updateTransaction, transaction],
  );

  const handleReset = useCallback(() => {
    form.resetFields();
  }, [form]);

  if (!transaction) return null;

  return (
    <Form<TransactionType>
      {...layout}
      form={form}
      name="payment-config-form"
      onFinish={handleUpdateConfig}
      style={{ maxWidth: 800 }}
      autoComplete="off"
      initialValues={transaction}
    >
      <Form.Item
        name="providerTransactionConfigId"
        label={messages["common.providerTransactionConfig"] as string}
        rules={[createYupSync({ schema: transactionSchema })]}
        hasFeedback
        extra={
          !transaction?.providerId ? (
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              {messages["common.pleaseFirstSelectProvider"] as string}
            </Typography.Text>
          ) : null
        }
      >
        <Select
          loading={!providerTransactionConfigs}
          options={providerTransactionConfigs?.data?.data?.map((config) => ({
            label: <ProviderTransactionConfigName config={config} />,
            text: Object.values(config).join(" - "),
            value: config.id,
          }))}
          showSearch
          optionFilterProp="text"
          filterOption={true}
          disabled={
            providerTransactionConfigsLoading ||
            providerTransactionConfigsFetching ||
            !transaction?.providerId
          }
          placeholder={
            providerTransactionConfigsLoading ||
            providerTransactionConfigsFetching
              ? (messages["common.loading"] as string)
              : (messages["common.selectOne"] as string)
          }
        />
      </Form.Item>

      <StyledFlex style={{ justifyContent: "end" }}>
        <Space>
          <Button
            htmlType="button"
            onClick={handleReset}
            disabled={isUpdating || !transaction?.providerId}
          >
            {messages["common.reset"] as string}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isUpdating}
            disabled={!transaction?.providerId}
          >
            {messages["common.configure"] as string}
          </Button>
        </Space>
      </StyledFlex>
    </Form>
  );
}
